import { useEffect, useState, useRef } from 'react';
import { useParams, Link, useSearchParams } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { mangaCategories, mangaList } from '@/mocks/manga';

function CategorySEO({ cat }: { cat: typeof mangaCategories[0] }) {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://sakuranga.com/' },
          { '@type': 'ListItem', position: 2, name: cat.name, item: `https://sakuranga.com/category/${cat.slug}` },
        ],
      },
      {
        '@type': 'CollectionPage',
        name: `${cat.name} - 樱花动漫之家日漫大全`,
        description: `樱花动漫之家${cat.name}分类 - ${cat.description}。浏览精选日本漫画，发现好看的连载日漫。`,
        url: `https://sakuranga.com/category/${cat.slug}`,
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function MangaCardItem({ manga }: { manga: typeof mangaList[0] }) {
  const [imageLoaded, setImageLoaded] = useState(false);

  return (
    <Link
      to={`/article/${manga.id}`}
      className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
    >
      <div className="relative aspect-[3/4] overflow-hidden">
        <img
          src={manga.cover}
          alt={manga.title}
          className={`w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ${
            imageLoaded ? 'opacity-100' : 'opacity-0'
          }`}
          onLoad={() => setImageLoaded(true)}
        />
        {!imageLoaded && (
          <div className="absolute inset-0 bg-background-200 animate-pulse" />
        )}
        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500/90 text-background-50 text-xs font-medium">
          {manga.rating}
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pt-8 pb-2 px-3">
          <span className="text-background-50 text-xs font-medium">{manga.status}</span>
        </div>
      </div>
      <div className="p-3">
        <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
          {manga.title}
        </h3>
        <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
        <div className="flex items-center gap-2 mt-2">
          <span className="text-xs text-foreground-400">{manga.chapters}话</span>
          <span className="text-xs text-foreground-300">·</span>
          <span className="text-xs text-foreground-400">{(manga.views / 10000).toFixed(0)}万人气</span>
        </div>
      </div>
    </Link>
  );
}

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const searchQuery = searchParams.get('search') || '';
  const topRef = useRef<HTMLDivElement>(null);

  const category = mangaCategories.find((c) => c.slug === slug);
  const activeCategory = category || mangaCategories[0];

  let filteredManga = mangaList.filter((m) => m.category === activeCategory.slug);
  if (searchQuery) {
    filteredManga = mangaList.filter(
      (m) =>
        m.title.includes(searchQuery) ||
        m.author.includes(searchQuery) ||
        m.tags.some((t) => t.includes(searchQuery))
    );
  }

  useEffect(() => {
    if (topRef.current) {
      topRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [slug, searchQuery]);

  return (
    <Layout>
      <CategorySEO cat={activeCategory} />
      <div ref={topRef} className="pt-20 pb-14 md:pb-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 mb-6 flex-wrap">
            <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">首页</Link>
            <span className="text-foreground-300">/</span>
            {searchQuery ? (
              <>
                <Link to={`/category/${activeCategory.slug}`} className="hover:text-primary-500 transition-colors cursor-pointer">
                  {activeCategory.name}
                </Link>
                <span className="text-foreground-300">/</span>
                <span className="text-foreground-700">搜索：{searchQuery}</span>
              </>
            ) : (
              <span className="text-foreground-700">{activeCategory.name}</span>
            )}
          </nav>

          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-950">
              {searchQuery ? `搜索：${searchQuery}` : activeCategory.name}
            </h1>
            <p className="text-sm text-foreground-500 mt-2">{activeCategory.description}</p>
          </div>

          {/* Category Tabs */}
          <div className="flex items-center gap-2 mb-8 overflow-x-auto pb-2 flex-wrap">
            {mangaCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-all ${
                  activeCategory.slug === cat.slug && !searchQuery
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className={`${cat.icon} text-xs`}></i>
                </div>
                {cat.name}
              </Link>
            ))}
          </div>

          {/* Results */}
          {filteredManga.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
              {filteredManga.map((manga) => (
                <MangaCardItem key={manga.id} manga={manga} />
              ))}
            </div>
          ) : (
            <div className="text-center py-16 md:py-20">
              <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200/70 text-foreground-400 mb-4">
                <i className="ri-file-search-line text-2xl"></i>
              </div>
              <h3 className="text-base font-heading font-semibold text-foreground-700 mb-1">暂无相关内容</h3>
              <p className="text-sm text-foreground-500">该分类下暂时没有漫画资源，请尝试浏览其他分类</p>
              <Link
                to="/"
                className="mt-4 inline-flex items-center gap-1.5 text-sm text-primary-500 hover:text-primary-600 font-medium cursor-pointer"
              >
                <div className="w-4 h-4 flex items-center justify-center">
                  <i className="ri-arrow-left-line text-sm"></i>
                </div>
                返回首页
              </Link>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}